<script setup>
    import { defineEmits, ref, inject, defineExpose, computed } from 'vue';
    import { useUserSelectStore } from '@/stores/modal/userSelectStore';
    import PaginationModal from '@/components/modal/PaginationComponentForModal.vue';
    import Utils from '@/commonProject/helpers/utils.js';
    import i18n from '@/common/i18n';

    // 従業員指定の場合
    const USER_SELECT_TYPE_1 = '1';

    // サービス管理者、産業保健スタッフ指定の場合
    const USER_SELECT_TYPE_2 = '2';

    // 管理者指定の場合
    const USER_SELECT_TYPE_3 = '3';

    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');

    // ストア情報取得
    const userSelectStore = useUserSelectStore();

    const keyword = ref('');
    const opeMode = ref('');

    // ページ関連
    const currentPage = ref(1);
    const totalPages = ref(0);
    const maxPage = 50;

    // 親画面から取得したAPIのドメイン部を管理する。
    const API_URL = ref('');

    // ユーザー選択モーダル処理の親画面処理を定義する。
    const emit = defineEmits(['userSelectModal:close', 'userSelectModal:select']);

    /**
     * 検索処理を実行する。
     */
    async function onSearch() {
        // バリデーションチェック
        if (isValidationError()) {
            return;
        }
        // ユーザー選択APIのリクエストボディを生成する。
        const queryParam = {
            Cond: {
                OpeMode: opeMode.value,
                KeyWord: keyword.value,
            },
            PageCond: {
                RowPosition: maxPage * (currentPage.value - 1) + 1,
                RowAmount: maxPage,
                RowCount: true,
            },
        };
        // ユーザー選択APIをコールする。
        const response = await userSelectStore.getUserSelectAction(API_URL.value, queryParam);

        if (response.success) {
            totalPages.value = userSelectStore.pageInfo;
            handleOnloadPagination(totalPages.value);
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
    }

    /**
     * 親画面から開示カウンセリング詳細モーダルを開くイベントが実行された場合の処理。
     * @param {String} trg HTMLファイルタグが生成されている座標を取得する。
     * @param {String} mode ユーザー選択範囲
     * @returns なし
     */
    async function openModal(trg, mode) {
        reset();
        // APIのURL情報を取得する。
        API_URL.value = trg;
        opeMode.value = mode;
        await onSearch();
    }

    /**
     * ページ移動
     * @param newPage
     */
    function handlePageChange(newPage) {
        currentPage.value = newPage;
        onSearch();
    }
    const paginationModal = ref();
    /**
     * ページングの初期処理を実行する。
     * 取得したAPIのデータ数を引き渡す。
     */
    function handleOnloadPagination(RowResult) {
        paginationModal.value.onload(RowResult);
    }

    /**
     * 親画面のユーザー選択モーダルクローズ処理を呼び出す。
     */
    function closeModal() {
        emit('userSelectModal:close');
    }

    /**
     * 親画面のユーザー選択モーダル選択処理を呼び出す。
     * @param UserPk
     * @param UserNm
     */
    function selectModal(UserPk, UserNm) {
        emit('userSelectModal:select', UserPk, UserNm);
    }

    const title = computed(() => {
        let tmp = i18n.global.t('userSelect.parts.modalTitle1');
        if (USER_SELECT_TYPE_1 === opeMode.value) {
            return tmp + i18n.global.t('userSelect.parts.modalTitleSub1');
        } else if (USER_SELECT_TYPE_2 === opeMode.value) {
            return tmp + i18n.global.t('userSelect.parts.modalTitleSub2');
        } else if (USER_SELECT_TYPE_3 === opeMode.value) {
            return tmp + i18n.global.t('userSelect.parts.modalTitleSub3');
        } else {
            return '';
        }
    });

    /**
     * バリデーションチェック
     */
    function isValidationError() {
        // エラーメッセージを初期化する。
        let message = '';

        // キーワードが入力されている場合は256文字以内であることをチェックする。
        if (!Utils.isNullEmpty(keyword.value) && Utils.checkLengthOfKeyword(keyword.value)) {
            message += i18n.global.t('userSelect.message.message1') + '\n';
        }
        // エラーメッセージが存在する場合は、以降の処理に進ませないので、バリデーションチェック判定を返却する。
        if (!Utils.isNullEmpty(message)) {
            // エラーメッセージが存在する場合は、画面に表示する。
            messageDialog('error', message);
            return true;
        }

        return false;
    }

    /**
     * 初期化する。
     */
    function reset() {
        opeMode.value = '';
        keyword.value = '';
        currentPage.value = 1;
        totalPages.value = 0;
        userSelectStore.reset();
    }

    /**
     * 親画面から実行を許可する本コンポーネント内のメソッドの一覧。
     */
    defineExpose({
        openModal,
    });
</script>

<style></style>

<template>
    <article id="modalUserSelection" class="modal" :style="{ display: 'block' }">
        <h3 class="textCenter">{{ title }}</h3>
        <section class="modalContents" style="height: calc(100% - 145px)">
            <ul style="display: flex; align-items: center; margin-bottom: 15px">
                <li>
                    <dl style="display: flex; align-items: center">
                        <dt style="margin-right: 5px">
                            <label>{{ $t('userSelect.parts.dl1Label1') }}</label>
                        </dt>
                        <dd class="searchFilter">
                            <ul class="filterInput searchBlock">
                                <li>
                                    <input type="text" class="text" v-model="keyword" />
                                </li>
                                <li>
                                    <button type="button" class="btnSearch" @click="onSearch">{{ $t('userSelect.parts.modalButton1') }}</button>
                                </li>
                            </ul>
                        </dd>
                    </dl>
                </li>
            </ul>
            <article class="innerTable" style="height: auto; margin-top: 20px; margin-bottom: 15px">
                <table class="table noSelection">
                    <thead>
                        <tr>
                            <th style="width: 80px">
                                <span>{{ $t('userSelect.parts.dl1Label2') }}</span>
                            </th>
                            <th style="width: 80px">
                                <span>{{ $t('userSelect.parts.dl1Label3') }}</span>
                            </th>
                            <th style="width: 110px">
                                <span>{{ $t('userSelect.parts.dl1Label4') }}</span>
                            </th>
                            <th style="width: 100%">
                                <span>{{ $t('userSelect.parts.dl1Label5') }}</span>
                            </th>
                            <th class="btnRow" style="width: 80px"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(list, index) in userSelectStore.UserList" :key="index">
                            <td>
                                <span>{{ list.UserId }}</span>
                            </td>
                            <td>
                                <span>{{ list.EmployeeNo }}</span>
                            </td>
                            <td>
                                <span>{{ list.UserNm }}</span>
                            </td>
                            <td>
                                <span>{{ list.MailAddress }}</span>
                            </td>
                            <td>
                                <button type="button" class="innerTableBtn btnSelect closeModal" id="UserA" @click="selectModal(list.UserPk, list.UserNm)">{{ $t('userSelect.parts.modalButton2') }}</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </article>
            <PaginationModal ref="paginationModal" :currentPage="currentPage" :totalPages="totalPages" @pageChanged="handlePageChange" />
        </section>
        <section class="modalFooter">
            <ul class="btnArea center">
                <li>
                    <button type="button" class="btnNormal closeModal" id="backToModalOpenCounselingServiceEntry" @click="closeModal">{{ $t('common.parts.cancel') }}</button>
                </li>
            </ul>
        </section>
    </article>
</template>
